import React, { useEffect, useMemo, useState } from 'react';
import { GetJson } from '@awp/helpers/fetchReact';
import { EnabledFeature, FORM_MODE, GeoAreaTypes, Permissions, REST_API } from '@awp/constants';
import * as Yup from 'yup';

import { Button, Card, Col, Container, FormCheck, FormGroup, Row, Spinner } from 'react-bootstrap';
import {
  FormCheckbox,
  FormDatePicker,
  FormInput,
  FormRadio,
  FormRadioGroup,
  FormSelect,
} from '@awp/components/Common/AwpFormInputs';
import { hasAccess } from '@components/Authorize';
import { Field, Form, Formik } from 'formik';

import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AlertGroup from './AlertGroup';
import { useNotificationContext } from './NotificationsContext';
import {
  allAreaTypesOptions,
  defaultDto,
  filterWeatherStations,
  frequencies,
  modeConfig,
  noDataFrequencies,
  SUBSCRIPTION_TYPE,
} from './NotificationCommon';
import { displayAlertLogic, entityToDto, transformFormikValues, YupValidation } from './NotificationEditHandlers';
import NotificationEditButtons from './NotificationEditButtons';
import { setContractArea, setEetArea } from '@awp/stores/weatherStationSlice';
import { Crud } from '@awp/shared/Crud';

/**
 * NotificationEdit component renders a form that allows users to Create, Edit or View Notifications.
 *
 * @returns {JSX.Element} The NotificationEdit component.
 */
const NotificationEdit = () => {
  const {
    data,
    setData,
    mode,
    setMode,
    notificationId,
    setNotificationId,
    loading,
    setLoading,
    currentUser,
    weatherStationsObsTypes,
    stationFilter,
    setStationFilter,
    errors,
    setErrors,
    notificationMethods,
    geographicAreaTypes,
    contractAreas,
    eetAreas,
  } = useNotificationContext();

  const [dto, setDto] = useState(defaultDto(notificationMethods)); // State for DTO
  const [dynamicFields, setDynamicFields] = useState([]); // State for dynamic fields
  const [rootGroup, setRootGroup] = useState(null); // State for root trigger group
  const [validationSchema, setValidationSchema] = useState(Yup.object); // State for validation schema
  const [editable, setEditable] = useState(true); // State for determining if form is editable
  const [geographicArea, setGeographicArea] = useState(null);
  const [selectedContractAreas, setSelectedContractAreas] = useState(null);
  const [selectedEETAreas, setSelectedEETAreas] = useState([]);
  const [subscriptionType, setSubscriptionType] = useState(SUBSCRIPTION_TYPE.WEATHER_STATION);

  const { readOnly, title, subscribeMode } = modeConfig[mode] || modeConfig.default; // Get form mode configuration
  const crud = new Crud('Alert', mode);
  crud.title = title;

  const isAdmin = hasAccess([Permissions.WritePublicAlert]); // Check if the user has admin access

  useEffect(() => {
    if (mode === FORM_MODE.CREATE) {
      // Initialize root group for a new notification
      setRootGroup({
        TriggerConditions: [
          { ObservationTypeId: null, ComparisonOperator: null, ComparisonValue: null, ObservationDataType: null },
        ],
        IsAnyCondition: 'N',
        ChildTriggerGroups: [],
      });
    } else {
      setLoading(true);
      const url = REST_API.NOTIFICATIONS + notificationId;

      // Fetch existing notification details for editing or cloning
      GetJson(url)
        .then((response) => response.json())
        .then((entity) => {
          if (mode == FORM_MODE.CLONE) {
            entity.NotificationTriggerId = 0; // Reset ID for cloning
            entity.Name = ''; // Clear name for cloning
            if (!isAdmin) {
              entity.IsPrivate = 'Y'; // Set to private if the user is not an admin
            }
          }
          if ((entity.IsPrivate == 'N' && !isAdmin) || entity.IsForMissingObservations === 'Y') {
            setEditable(false); // Make form non-editable based on conditions
          }
          setDto(
            entityToDto(entity, notificationMethods, frequencies, weatherStationsObsTypes, contractAreas, eetAreas),
          ); // Set DTO state

          setRootGroup(entity.TriggerGroup); // Set root group state
          setLoading(false);
        });
    }
  }, []);

  useMemo(() => {
    setGeographicArea(dto.geographicAreaType);
    setEetArea(dto.geographicEETAreas);
    setContractArea(dto.geographicContractArea);

    if (dto.areaType === 'geographicArea') {
      setSubscriptionType(SUBSCRIPTION_TYPE.GEOGRAPHIC_AREA);
    }
  }, [dto]);

  useEffect(() => {
    setValidationSchema(YupValidation(dynamicFields, dto, mode)); // Update validation schema when dynamic fields change
  }, [dynamicFields]);

  const handleGeographicAreaChange = (e) => {
    //reset area selections
    setEetArea(null);
    setContractArea(null);
    //set the geographic area
    setGeographicArea(e);
  };

  return (
    <>
      {loading ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <Formik
            enableReinitialize
            validateOnMount
            validateOnBlur
            validateOnChange
            initialValues={dto}
            validationSchema={validationSchema}
          >
            {(props) => (
              <>
                {/* {props.errors && <pre>{JSON.stringify(props.errors, null, 2)}</pre>} */}
                {/* {props.values && <pre>{JSON.stringify(props.values, null, 2)}</pre>} */}
                <Form id="notification">
                  <Card
                    border="light"
                    className="page-card"
                    style={{ border: '0px', boxShadow: '0px 0px 0px #000000' }}
                  >
                    <Card.Header style={{ backgroundColor: 'white' }}>
                      <Row>
                        <Col>{crud.getHeader()}</Col>
                        <Col>
                          {(editable || (isAdmin && !dto.isForMissingObservations)) && mode === 'view' && (
                            <div className="mr-3" style={{ float: 'right' }}>
                              <Button className="mr-2" variant="outline-primary" onClick={() => setMode('edit')}>
                                Edit Alert
                              </Button>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Card.Header>
                    <Card.Body>
                      <FormInput
                        readOnly={readOnly || subscribeMode}
                        name="name"
                        id="name"
                        label="Name"
                        instruction="(required, 100 characters max)"
                      />
                      <FormCheckbox readOnly={readOnly || subscribeMode} id="isActive" name="isActive" label="Active" />
                      <FormCheckbox
                        readOnly={readOnly || subscribeMode}
                        id="enableDateRange"
                        name="enableDateRange"
                        label="Enable Active Date Range"
                        className="mt-3"
                      />
                      {props.values.enableDateRange && (
                        <>
                          <div className="customWidthDatePicker">
                            <FormDatePicker
                              readOnly={readOnly || subscribeMode}
                              label="Start Date"
                              id="startDate"
                              name="startDate"
                              dateFormat={['MMM dd']}
                              instruction="(required)"
                            />
                          </div>
                          <div className="customWidthDatePicker">
                            <FormDatePicker
                              readOnly={readOnly || subscribeMode}
                              label="End Date"
                              id="endDate"
                              name="endDate"
                              dateFormat={['MMM dd']}
                              instruction="(required)"
                            />
                          </div>
                        </>
                      )}
                      {(props.values.isPrivate === 'Y' || subscribeMode) && (
                        <>
                          <h4 className="mt-3 mb-2">Notifications</h4>
                          <FormCheckbox
                            readOnly={readOnly}
                            id="email"
                            name="email"
                            label="Email"
                            className="d-inline-block mr-3"
                            disabled
                          />
                          <FormCheckbox
                            readOnly={readOnly}
                            id="sms"
                            name="sms"
                            label="SMS"
                            className="d-inline-block user-select-none"
                          />
                        </>
                      )}

                      <FormSelect
                        readOnly={readOnly || (subscribeMode && !dto.isForMissingObservations)}
                        name="frequency"
                        id="frequency"
                        label="Frequency"
                        options={dto.isForMissingObservations ? noDataFrequencies : frequencies}
                        isMulti={false}
                        instruction="(required)"
                      />
                      <FormRadioGroup
                        className="mt-3"
                        label="Visibility"
                        name="isPrivate"
                        id="isPrivate"
                        readOnly={readOnly || mode == FORM_MODE.EDIT || subscribeMode}
                        textValue={props.values.isPrivate === 'Y' ? 'Private' : 'Public'}
                      >
                        <Row>
                          <Col sm={2} md={2} lg={1}>
                            <FormRadio id="private" label="Private" name="isPrivate" value="Y" />
                          </Col>
                          {isAdmin && (
                            <Col sm={2} md={2} lg={1}>
                              <FormRadio id="public" label="Public" name="isPrivate" value="N" />
                            </Col>
                          )}
                        </Row>
                      </FormRadioGroup>
                      <div className="form-group mt-3">
                        <h4>Alert Logic</h4>
                        {!dto.isForMissingObservations && props.values.TriggerGroup && (
                          <span>{displayAlertLogic(transformFormikValues(props.values).TriggerGroup)}</span>
                        )}
                        {dto.isForMissingObservations && <span>{'Partial/No Data'}</span>}
                      </div>
                      <div className={readOnly || subscribeMode ? 'awp-hidden' : ''}>
                        <AlertGroup
                          level={1}
                          name={'0'}
                          groupName={'1'}
                          onDelete={() => {}}
                          dynamicFields={dynamicFields}
                          setDynamicFields={setDynamicFields}
                          groupData={rootGroup}
                          setFieldValue={props.setFieldValue}
                          values={props.values}
                          props={props}
                        />
                      </div>
                      {(props.values.isPrivate === 'Y' || subscribeMode) && (
                        <FormGroup className="mt-3" name="areaType" id="areaType">
                          <Row>
                            <Col>
                              <Row>
                                {!readOnly ? (
                                  <Col className="form-group" style={{ marginBottom: '0px !important' }}>
                                    <Field name="areaType">
                                      {({ field }) => (
                                        <FormCheck
                                          {...field}
                                          id="areaType"
                                          type="radio"
                                          label="Weather Station"
                                          value="weatherStation"
                                          checked={subscriptionType === SUBSCRIPTION_TYPE.WEATHER_STATION}
                                          onChange={(e) => {
                                            field.onChange(e);
                                            props.setFieldValue('geographicAreaType', null, false);
                                            props.setFieldValue('geographicEETAreas', [], false);
                                            props.setFieldValue('geographicContractAreas', [], false);
                                            setGeographicArea(null);
                                            setSubscriptionType(SUBSCRIPTION_TYPE.WEATHER_STATION);
                                          }}
                                        />
                                      )}
                                    </Field>
                                  </Col>
                                ) : (
                                  <Col className="form-group" style={{ marginBottom: '0px !important' }}>
                                    <b>Weather Stations</b>
                                  </Col>
                                )}
                              </Row>
                              {!readOnly && (
                                <Row>
                                  <Col md={1}>
                                    <div dangerouslySetInnerHTML={{ __html: '&nbsp;' }}></div>
                                  </Col>
                                  <Col>
                                    <FormGroup>
                                      <Row>
                                        <Col>
                                          <FormCheck
                                            readOnly={subscriptionType !== SUBSCRIPTION_TYPE.WEATHER_STATION}
                                            type="radio"
                                            id="filteredStaions"
                                            name="filteredStaions"
                                            value={true}
                                            label="Stations with sensor placement"
                                            checked={stationFilter}
                                            onChange={() => setStationFilter(!stationFilter)}
                                          />
                                        </Col>
                                        <Col>
                                          <FormCheck
                                            readOnly={subscriptionType !== SUBSCRIPTION_TYPE.WEATHER_STATION}
                                            type="radio"
                                            id="allStations"
                                            name="allStations"
                                            label="All stations"
                                            checked={!stationFilter}
                                            onChange={() => setStationFilter(!stationFilter)}
                                          />
                                        </Col>
                                      </Row>
                                    </FormGroup>
                                  </Col>
                                </Row>
                              )}
                              <Row>
                                <Col md={1}>
                                  <div dangerouslySetInnerHTML={{ __html: '&nbsp;' }}></div>
                                </Col>
                                <Col>
                                  <FormSelect
                                    isDisabled={readOnly || subscriptionType !== SUBSCRIPTION_TYPE.WEATHER_STATION}
                                    id="weatherStations"
                                    name="weatherStations"
                                    label="Weather Stations"
                                    textValue=""
                                    options={filterWeatherStations(props.values)}
                                    isMulti={true}
                                    instruction={
                                      subscriptionType === SUBSCRIPTION_TYPE.WEATHER_STATION ? '(required)' : ''
                                    }
                                    closeMenuOnSelect={false}
                                  />
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                          {EnabledFeature.AlertGeoArea && (
                            <>
                              <Row>
                                {!readOnly ? (
                                  <Col className="form-group">
                                    <Field name="areaType">
                                      {({ field }) => (
                                        <FormCheck
                                          type="radio"
                                          id="areaType"
                                          label="Geographic Area"
                                          value="geographicArea"
                                          checked={subscriptionType === SUBSCRIPTION_TYPE.GEOGRAPHIC_AREA}
                                          onChange={(e) => {
                                            field.onChange(e);
                                            props.setFieldValue('weatherStations', [], false);
                                            setSubscriptionType(SUBSCRIPTION_TYPE.GEOGRAPHIC_AREA);
                                          }}
                                        />
                                      )}
                                    </Field>
                                  </Col>
                                ) : (
                                  <Col className="form-group" style={{ marginBottom: '0px !important' }}>
                                    <b>Geographic Area</b>
                                  </Col>
                                )}
                              </Row>

                              <Row>
                                <Col md={1}>
                                  <div dangerouslySetInnerHTML={{ __html: '&nbsp;' }}></div>
                                </Col>
                                <Col>
                                  <FormSelect
                                    isDisabled={readOnly || subscriptionType !== SUBSCRIPTION_TYPE.GEOGRAPHIC_AREA}
                                    id="geographicAreaType"
                                    name="geographicAreaType"
                                    label="Geographic Area Type"
                                    textValue=""
                                    options={allAreaTypesOptions}
                                    postChange={(e) => {
                                      props.setFieldValue('geographicEETAreas', [], false);
                                      props.setFieldValue('geographicContractAreas', [], false);
                                      handleGeographicAreaChange(e);
                                    }}
                                    isMulti={false}
                                    instruction={
                                      subscriptionType === SUBSCRIPTION_TYPE.GEOGRAPHIC_AREA ? '(required)' : ''
                                    }
                                    closeMenuOnSelect={true}
                                  />
                                </Col>
                              </Row>
                              <Row>
                                <Col md={1}>
                                  <div dangerouslySetInnerHTML={{ __html: '&nbsp;' }}></div>
                                </Col>
                                <Col>
                                  {geographicArea && geographicArea.value === GeoAreaTypes.EET && (
                                    <FormSelect
                                      isDisabled={readOnly || subscriptionType !== SUBSCRIPTION_TYPE.GEOGRAPHIC_AREA}
                                      id="geographicEETAreas"
                                      name="geographicEETAreas"
                                      label={GeoAreaTypes.EET_DISPLAY}
                                      textValue=""
                                      options={eetAreas}
                                      isMulti={true}
                                      instruction={
                                        subscriptionType === SUBSCRIPTION_TYPE.GEOGRAPHIC_AREA ? '(required)' : ''
                                      }
                                      closeMenuOnSelect={false}
                                    />
                                  )}
                                  {geographicArea && geographicArea.value === GeoAreaTypes.CONTRACT && (
                                    <FormSelect
                                      isDisabled={readOnly || subscriptionType !== SUBSCRIPTION_TYPE.GEOGRAPHIC_AREA}
                                      id="geographicContractAreas"
                                      name="geographicContractAreas"
                                      label={GeoAreaTypes.CONTRACT_DISPLAY}
                                      textValue=""
                                      options={contractAreas}
                                      isMulti={true}
                                      instruction={
                                        subscriptionType === SUBSCRIPTION_TYPE.GEOGRAPHIC_AREA ? '(required)' : ''
                                      }
                                      closeMenuOnSelect={false}
                                    />
                                  )}
                                </Col>
                              </Row>
                            </>
                          )}
                        </FormGroup>
                      )}
                      <NotificationEditButtons title=" " props={props} subscribeMode={subscribeMode} />
                    </Card.Body>
                  </Card>
                </Form>
              </>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setErrors} errors={errors} />
        </>
      )}
    </>
  );
};

export default NotificationEdit;
